import React from "react";
import "./access-group-component.css";
import { AccessGroupsData } from "../../api/models/access-groups";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";
import { useAppSelector } from "../../app/hooks";
import { selectLabels } from "../../slices/language";
import IconifyIcon from "../IconifyIcon";
import { RequirementTypesData } from "../../api/models/requirement-types";

interface AccessGroupComponentProps {
  accessGroup: AccessGroupsData;
  blockFunction: () => void;
  seeBlockedUsersFunc: () => void;
  deleteFunction: () => void;
  editFunction: () => void;
  hasUserAccessGroups: boolean;
  hasExitRestriction: boolean;
}

const formatTime = (time?: string | null) => {
  if (!time) return "-";
  return time.slice(0, 5);
};

const isAllDaySchedule = (
  startTime?: string | null,
  endTime?: string | null,
) => {
  if (!startTime || !endTime) return false;

  const start = startTime.slice(0, 5);
  const end = endTime.slice(0, 5);

  return start === "00:00" && end === "23:59";
};

const AccessGroupComponent = ({
  accessGroup,
  blockFunction,
  seeBlockedUsersFunc,
  deleteFunction,
  editFunction,
  hasUserAccessGroups,
  hasExitRestriction,
}: AccessGroupComponentProps) => {
  const labels = useAppSelector(selectLabels);
  const [requirementsModalOpen, setRequirementsModalOpen] =
    React.useState(false);
  const [usersModalOpen, setUsersModalOpen] = React.useState(false);

  const requirementTypes =
    accessGroup?.requirement_types ?? [];

  const accessGroupUsers = React.useMemo(() => {
    return (
      accessGroup?.user_access_groups
        ?.map((userAccessGroup) => userAccessGroup?.user)
        .filter((user) => !!user) ?? []
    );
  }, [accessGroup?.user_access_groups]);

  const sortedData = [...(accessGroup?.configuration ?? [])].sort(
    (a, b) => a.id - b.id,
  );

  return (
    <div
      className="w-100 border-box hidden flex flex-dir-col align-items-center gap-4 p-8"
      style={{
        backgroundColor: "white",
        border: "1px #c4c4c4 solid",
        borderRadius: 6,
        boxShadow: "rgba(99, 99, 99, 0.2) 0px 2px 8px 0px",
      }}
    >
      <div className="w-100">
        <div className="w-100 flex align-items-center justify-content-between">
          <div className="flex align-items-center gap-2">
            <p className="t-nowrap body-text-lg font-w-500 title-color">
              {accessGroup?.name}
            </p>
            {requirementTypes.length > 0 && (
              <Tooltip title={labels.requirement_types_tooltip}>
                <Button
                  size="small"
                  variant="outlined"
                  color="primary"
                  startIcon={<IconifyIcon icon={"mdi:format-list-checks"} />}
                  onClick={() => setRequirementsModalOpen(true)}
                  aria-label={labels.requirement_types}
                  style={{ marginLeft: 8 }}
                >
                  {labels.registration_requirements}
                </Button>
              </Tooltip>
            )}
          </div>
          <div className="flex flex-dir-col align-items-end gap-1">
            <div className="flex align-items-center gap-3">
              <IconButton color="primary" onClick={editFunction}>
                <IconifyIcon icon={"fa-solid:edit"} />
              </IconButton>
              <IconButton color="primary" onClick={deleteFunction}>
                <IconifyIcon icon={"fa-solid:trash"} />
              </IconButton>
            </div>
          </div>
        </div>
        <div className="w-100 flex align-items-center justify-content-between mt-3">
          <div className="flex align-items-center gap-3">
            <p className="t-nowrap body-text-sm mb-3">
              {labels.applied_to_roles}:
            </p>
            {accessGroup?.unit_access_group && (
              <p className="t-nowrap body-text-sm mb-3">
                {labels.secondary_users}
              </p>
            )}
            {accessGroup?.role_permissions_users?.map(
              (item, index) => (
                <p
                  key={`${item?.id}-${index}`}
                  className="t-nowrap body-text-sm mb-3"
                >
                  {item?.name.startsWith("host") &&
                    !accessGroup?.unit_access_group &&
                    labels.users}
                  {item?.name.startsWith("property") &&
                    !accessGroup?.unit_access_group &&
                    labels.admins}
                  {item?.name.startsWith("secondary") &&
                    !accessGroup?.unit_access_group &&
                    labels.secondary_users}
                  {item?.name.startsWith("visitor") &&
                    !accessGroup?.unit_access_group &&
                    labels.visitors}
                  {!accessGroup?.unit_access_group &&
                    accessGroup?.role_permissions_users
                      .length -
                      1 !==
                      index &&
                    ","}
                </p>
              ),
            )}
          </div>
          {hasUserAccessGroups && (
              <Button
                size="small"
                variant="text"
                onClick={() => setUsersModalOpen(true)}
              >
                {labels.see_users}
              </Button>
            )}
          {/* <div
            className="flex align-items-center gap-3 mr-2"
            style={{ cursor: "pointer" }}
            onClick={seeBlockedUsersFunc}
          >
            <p
              className="t-nowrap body-text-sm mb-3"
              style={{ color: "#10418a", fontWeight: 500 }}
            >
              {labels.blocked_users}:
            </p>
            <p
              className="t-nowrap body-text-sm mb-3"
              style={{ color: "#10418a", fontWeight: 500 }}
            >
              {accessGroup?.attributes?.banned_users?.data?.length ?? 0}
            </p>
          </div> */}
        </div>
      </div>
      <Box
        style={{
          width: "100%",
          boxSizing: "border-box",
          border: "1px #e0e0e0 solid",
          borderRadius: 6,
          overflow: "hidden",
        }}
      >
        <TableContainer
          style={{ overflowX: "auto", backgroundColor: "#f8f8f8" }}
        >
          <Table stickyHeader style={{ width: "100%" }}>
            <TableHead>
              <TableRow>
                <TableCell
                  align="center"
                  className="p-5"
                  style={{ backgroundColor: "#f0f0f0" }}
                >
                  <p>{labels.access_point}</p>
                </TableCell>
                <TableCell
                  align="center"
                  className="p-5"
                  style={{ backgroundColor: "#f0f0f0" }}
                >
                  <p>{labels.description}</p>
                </TableCell>
                <TableCell
                  align="center"
                  className="p-5"
                  style={{ backgroundColor: "#f0f0f0" }}
                >
                  <p>{labels.days_allowed}</p>
                </TableCell>
                <TableCell
                  align="center"
                  className="p-5"
                  style={{ backgroundColor: "#f0f0f0" }}
                >
                  <p>
                    {hasExitRestriction
                      ? labels.entry_schedule
                      : labels.from_ag}
                  </p>
                </TableCell>
                <TableCell
                  align="center"
                  className="p-5"
                  style={{ backgroundColor: "#f0f0f0" }}
                >
                  <p>
                    {hasExitRestriction
                      ? labels.permitted_exit_schedule
                      : labels.to_ag}
                  </p>
                </TableCell>
                <TableCell
                  align="center"
                  className="p-5"
                  style={{ backgroundColor: "#f0f0f0" }}
                >
                  <p>{labels.required_photographs}</p>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {accessGroup?.configuration &&
                accessGroup?.configuration.length > 0 &&
                sortedData.map((item, index) => (
                  <TableRow
                    key={`${item?.id}-${index}`}
                    className={"no-selectable-row"}
                  >
                    <TableCell
                      align="center"
                      className="p-5"
                      sx={{
                        borderBottom:
                          accessGroup?.configuration.length - 1 ===
                          index
                            ? "none"
                            : "",
                      }}
                    >
                      {
                        accessGroup?.access_points?.filter(
                          (accPoint) => accPoint?.id === item?.id,
                        )[0]?.name
                      }
                    </TableCell>
                    <TableCell
                      align="center"
                      className="p-5"
                      sx={{
                        borderBottom:
                          accessGroup?.configuration.length - 1 ===
                          index
                            ? "none"
                            : "",
                      }}
                    >
                      {item?.description}
                    </TableCell>
                    <TableCell
                      align="center"
                      className="p-5"
                      sx={{
                        borderBottom:
                          accessGroup?.configuration.length - 1 ===
                          index
                            ? "none"
                            : "",
                      }}
                    >
                      {[
                        "monday",
                        "tuesday",
                        "wednesday",
                        "thursday",
                        "friday",
                        "saturday",
                        "sunday",
                      ].every((day) => item?.week_days?.includes(day)) ? (
                        <p>{labels.every_day}</p>
                      ) : (
                        <div>
                          {item?.week_days?.map((day, dayIndex) => (
                            <p key={`${day}-${dayIndex}`}>
                              {day === "monday" && labels.monday_2}
                              {day === "tuesday" && labels.tuesday_2}
                              {day === "wednesday" && labels.wednesday_2}
                              {day === "thursday" && labels.thursday_2}
                              {day === "friday" && labels.friday_2}
                              {day === "saturday" && labels.saturday_2}
                              {day === "sunday" && labels.sunday_2}
                            </p>
                          ))}
                        </div>
                      )}
                    </TableCell>
                    <TableCell
                      align="center"
                      className="p-5"
                      sx={{
                        borderBottom:
                          accessGroup?.configuration.length - 1 ===
                          index
                            ? "none"
                            : "",
                      }}
                    >
                      {hasExitRestriction
                        ? isAllDaySchedule(item?.start_time, item?.end_time)
                          ? labels.all_day
                          : `${formatTime(item?.start_time)} - ${formatTime(item?.end_time)}`
                        : isAllDaySchedule(item?.start_time, item?.end_time)
                          ? labels.all_day
                          : formatTime(item?.start_time)}
                    </TableCell>
                    <TableCell
                      align="center"
                      className="p-5"
                      sx={{
                        borderBottom:
                          accessGroup?.configuration.length - 1 ===
                          index
                            ? "none"
                            : "",
                      }}
                    >
                      {hasExitRestriction
                        ? item?.exit_restriction_from ||
                          item?.exit_restriction_to ||
                          item?.exit_restriction
                          ? `${formatTime(
                              item?.exit_restriction_from ||
                                item?.exit_restriction,
                            )} - ${formatTime(item?.exit_restriction_to)}`
                          : "-"
                        : isAllDaySchedule(item?.start_time, item?.end_time)
                          ? "-"
                          : formatTime(item?.end_time)}
                    </TableCell>
                    <TableCell
                      align="center"
                      className="p-5"
                      sx={{
                        borderBottom:
                          accessGroup?.configuration.length - 1 ===
                          index
                            ? "none"
                            : "",
                      }}
                    >
                      {(item?.photos?.length ?? 0) > 0 ? (
                        <div>
                          {item?.photos?.map((photo, photoIndex) => (
                            <p key={`${photo}-${photoIndex}`}>
                              {photo === "id" && labels.id}
                              {photo === "plates" && labels.plates}
                              {photo === "visitor" && labels.portrait}
                              {photo === "other" && labels.equipment}
                            </p>
                          ))}
                        </div>
                      ) : (
                        <p>-</p>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <Dialog
        open={requirementsModalOpen}
        onClose={() => setRequirementsModalOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <span>{labels.requirement_details}</span>
          <IconButton
            size="small"
            onClick={() => setRequirementsModalOpen(false)}
            aria-label={labels.cancel}
          >
            <IconifyIcon icon={"mingcute:close-fill"} />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {requirementTypes.length === 0 ? (
            <p className="body-text-sm">—</p>
          ) : (
            <div className="flex flex-dir-col gap-4">
              {requirementTypes.map(
                (req: RequirementTypesData, index: number) => (
                  <Box
                    key={`${req?.id}-${index}`}
                    sx={{
                      p: 2,
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 1,
                    }}
                  >
                    <p className="body-text-sm font-w-500 mb-2">
                      {labels.requirement_name}: {req?.name}
                    </p>
                    {req?.description && (
                      <p className="body-text-sm mb-2">
                        {labels.requirement_description}:{" "}
                        {req.description}
                      </p>
                    )}
                    <p className="body-text-sm mb-2">
                      {labels.requirement_data_type}:{" "}
                      {req?.data_type === "string"
                        ? labels.text
                        : req?.data_type === "selector"
                          ? labels.selector
                          : labels.date}
                    </p>
                    <p className="body-text-sm">
                      {labels.requirement_required}:{" "}
                      {req?.required ? labels.yes : labels.no}
                    </p>
                  </Box>
                ),
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={usersModalOpen}
        onClose={() => setUsersModalOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <span>{labels.access_group_users}</span>
          <IconButton
            size="small"
            onClick={() => setUsersModalOpen(false)}
            aria-label={labels.cancel}
          >
            <IconifyIcon icon={"mingcute:close-fill"} />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {accessGroupUsers.length === 0 ? (
            <p className="body-text-sm">{labels.no_users_in_access_group}</p>
          ) : (
            <div className="flex flex-dir-col gap-3">
              {accessGroupUsers.map((user, userIndex) => (
                <Box
                  key={`${user?.id}-${userIndex}`}
                  sx={{
                    p: 2,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1,
                  }}
                >
                  <p className="body-text-sm font-w-500">{user?.name}</p>
                  {user?.email && (
                    <p className="body-text-sm title-color">
                      {user.email}
                    </p>
                  )}
                </Box>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AccessGroupComponent;
